import _ from 'axios';
import configs from '../../../conf';
import { Auth } from 'aws-amplify';
import axiosRetry from 'axios-retry/index';

const config = configs[process.env.APP_CONF]; // @TODO now it uses development on local, so make sure NODE_ENV is set

const apiVersion = 'v4';
const api = `${config.cms}`;

export const ScenesType = {
  REGULAR: 'regular',
  ADAPTOR: 'adaptor',
};

_.interceptors.response.use(
  response => response,
  error => {
    const originalRequest = error.config;
    if (error.response?.status === 500 && !originalRequest._retry) {
      originalRequest._retry = true;
      return _(originalRequest);
    }
    return Promise.reject(error);
  }
);

axiosRetry(_, {
  retries: 3,
  retryDelay: retryCount => retryCount * 1000,
});

// Get access token for CMS API
// TODO: Check if token is valid, only if not valid, get a new one

let accessToken = {};
let requestConfig = {};

async function token() {
  const response = await _.get('/token');
  if (response) {
    accessToken = { ...response.data, updatedAt: Date.now() };
    requestConfig = {
      headers: {
        Authorization: 'bearer ' + response.data.access_token,
      },
    };
  }
}

token();

/**
 * Implementation of CmsApi.kt in mobile app project
 */
class CmsAPI {
  static async checkAccessToken() {
    if (Date.now() - accessToken.updatedAt > accessToken.expires_in * 1000) {
      await token();
    }
  }

  static async validIdToken() {
    let token = await this.getAccessJwtToken();

    if (token) {
      return {
        headers: {
          Authorization: 'bearer ' + token,
        },
      };
    } else {
      return {};
    }
  }

  static async getAccessJwtToken() {
    // Auth.currentSession() checks if token is expired and refreshes with Cognito if needed automatically

    // Check if user has logged in with SSO
    if (localStorage.getItem('aws-amplify-federatedInfo') !== null) {
      const currentAuthenticatedUser = await Auth.currentAuthenticatedUser();
      console.log('currentAuthenticatedUser', currentAuthenticatedUser);
      return currentAuthenticatedUser.token;
    } else {
      if (JSON.parse(localStorage.getItem('configured'))) {
        const session = await Auth.currentSession();
        console.log('session', session);
        return session.idToken.jwtToken;
      } else {
        return null;
      }
    }
  }

  /**
   * Create a story by starting the render pipeline
   * @param {string} storyId
   * @param {string} sceneId
   * @param {string} videoName
   * @param {string} videoKey
   * @param {int} videoDuration Optional video duration in milliseconds
   * @param {bool} trim True for regular render, trim to render a story based on trimmed scenes
   * @returns {object}
   */
  static async renderStory(
    storyId,
    sceneId,
    videoName,
    videoPath,
    videoDuration = null,
    trim = false,
    trimStart = null,
    trimEnd = null
  ) {
    let createDate = new Date();

    const story = {
      sceneId: sceneId,
      storyName: videoName,
      videoPath: videoPath,
      createDate: createDate.toISOString(),
      videoDuration: videoDuration,
      trim: trim,
      trimStart: trimStart,
      trimEnd: trimEnd,
    };

    let headerConfig = await this.validIdToken();
    return _.post(`${api}/${apiVersion}/story/${storyId}/render`, story, headerConfig).then(response => response.data);
  }

  static async getRenderStatus(storyId) {
    let headerConfig = await this.validIdToken();

    return _.get(`${api}/${apiVersion}/story/${storyId}/render`, headerConfig).then(response => response.data);
  }

  static async getProtectedStory(storyId, password, isAuth) {
    let headerConfig = {};
    if (isAuth) {
      headerConfig = await this.validIdToken();
    }

    return _.post(`${api}/${apiVersion}/stories/${storyId}/protected`, { password }, headerConfig).then(response => response.data);
  }

  static async getAwsConfigForCompany(companyId) {
    return _.get(`${api}/${apiVersion}/config/aws?companyId=${companyId}`, requestConfig).then(response => response.data);
  }

  static async getAwsConfigForDomain(domain, jwt = null) {
    if (jwt) {
      let headerConfig = {
        headers: {
          Authorization: 'bearer ' + jwt,
        },
      };

      return _.get(`${api}/${apiVersion}/config/aws?domain=${domain}`, headerConfig).then(response => response.data);
    } else {
      return _.get(`${api}/${apiVersion}/config/aws?domain=${domain}`, requestConfig).then(response => response.data);
    }
  }

  static async getAwsConfigForUsername(username) {
    await this.checkAccessToken();
    // username parameter can be UUID or Email
    return _.get(`${api}/${apiVersion}/config/aws?username=${encodeURIComponent(username)}`, requestConfig).then(
      response => response.data
    );
  }

  static async getTemplates(companyId, languageId) {
    return _.get(`${api}/${apiVersion}/subjects?companyId=${companyId}&language=${languageId}`, requestConfig).then(
      response => response.data
    );
  }

  static async getTemplateInfo(templateId, languageId) {
    return _.get(`${api}/${apiVersion}/subjects/questions?subjectId=${templateId}&language=${languageId}`, requestConfig).then(
      response => response.data
    );
  }

  static async getAppConfig(username) {
    await this.checkAccessToken();
    return _.get(`${api}/${apiVersion}/config/app/${encodeURIComponent(username)}`, requestConfig)
      .then(response => response.data)
      .catch(e => console.log(e));
  }

  static async defaultCompany(username) {
    this.checkAccessToken();
    return _.get(`${api}/${apiVersion}/company/default?userType=${username}`, requestConfig).then(response => response.data);
  }

  static async confirmEmail(email, code) {
    let tokenResponse = await _.get('/token');

    requestConfig = {
      headers: {
        Authorization: 'bearer ' + tokenResponse.data.access_token,
      },
    };

    const formData = new FormData();
    formData.append('username', email);
    formData.append('code', code);

    return _.post(`${api}/${apiVersion}/confirm-email`, formData, requestConfig)
      .then(response => response.data)
      .catch(error => error.response.data);
  }

  static async resendConfirmEmail(email) {
    const formData = new FormData();
    formData.append('username', email);
    return _.post(`${api}/${apiVersion}/resend-confirm-email`, formData, requestConfig).then(response => response.data);
  }

  static async forgotPassword(email) {
    this.checkAccessToken();
    const formData = new FormData();
    formData.append('email', email);
    return _.post(`${api}/${apiVersion}/forgot-password`, formData, requestConfig).then(response => response.data);
  }

  static async updatePassword(username, code, newPassword) {
    this.checkAccessToken();
    const formData = new FormData();
    formData.append('username', username);
    formData.append('code', code);
    formData.append('newPassword', newPassword);
    return _.post(`${api}/${apiVersion}/confirm-forgot-password`, formData, requestConfig).then(response => response.data);
  }

  static async isEmailAvailable(email) {
    this.checkAccessToken();
    return _.get(`${api}/${apiVersion}/email-available?email=${email}`, requestConfig).then(response => response.data);
  }

  static async terms(companyId) {
    return _.get(`${api}/${apiVersion}/company/terms?companyId=${companyId}`, requestConfig).then(response => response.data);
  }

  static async updateSubtitleVisibility(storyId, subtitle) {
    const headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/story/${storyId}/subtitles/visibility`, subtitle, headerConfig).then(response => response.data);
  }

  static async getMessages(params) {
    let config = await this.validIdToken();

    config.params = {};

    if (params?.before) {
      config.params.before = params.before;
    }

    if (params?.read === 1 || params?.read === 0) {
      config.params.read = params.read;
    }

    if (params?.type) {
      config.params.type = params?.type;
    }

    return _.get(`${api}/${apiVersion}/messages/list`, config).then(response => response.data);
  }

  static async updateMessage(id, message) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/messages/${id}`, message, headerConfig).then(response => response.data);
  }

  static async batchMessages(params) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/messages`, params, headerConfig).then(response => response.data);
  }

  static async getUser() {
    let headerConfig = await this.validIdToken();
    return _.get(`${api}/${apiVersion}/user`, headerConfig).then(response => response.data);
  }

  static async getCognitoUser() {
    console.log('will request getCognitoUser');
    return Auth.currentAuthenticatedUser({ bypassCache: true });
  }

  /**
   * @return {Promise<boolean>}
   */
  static async isLoggedIn() {
    if (JSON.parse(localStorage.getItem('configured'))) {
      try {
        await this.validIdToken();
        console.log('will request  isLoggedIn');
        await Auth.currentAuthenticatedUser();
        return true;
      } catch (e) {
        console.log('Not logged in', e);
        return false;
      }
    } else {
      return false;
    }
  }

  static async updateUser(user) {
    let headerConfig = await this.validIdToken();
    headerConfig.headers['Content-Type'] = 'application/x-www-form-urlencoded';

    const params = new URLSearchParams();

    const allowedKeys = [
      'username',
      'firstName',
      'lastName',
      'name',
      'language',
      'optins',
      'optins[inbox]',
      'optins[video_ready]',
      'optins[video_viewed]',
      'optins[video_first_viewed]',
      'avatar',
      'onboarded',
    ];

    for (const [key, value] of Object.entries(user)) {
      if (allowedKeys.includes(key)) {
        params.append(key, value);
      }
    }

    return _.put(`${api}/${apiVersion}/user`, params, headerConfig).then(response => response.data);
  }

  static async deleteUser() {
    let headerConfig = await this.validIdToken();
    return _.delete(`${api}/${apiVersion}/user`, headerConfig).then(response => response.data);
  }

  static async updateUserSetting(setting) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/user/setting`, setting, headerConfig).then(response => response.data);
  }

  static async getNotificationSettings() {
    let headerConfig = await this.validIdToken();
    return _.get(`${api}/${apiVersion}/user/notification-settings`, headerConfig).then(response => response.data);
  }

  static async updateNotificationSettings(settings) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/user/notification-settings`, settings, headerConfig).then(response => response.data);
  }

  static async getStory(storyId, sceneTypes = null) {
    let headerConfig = {};

    try {
      headerConfig = await this.validIdToken();
    } catch {
      console.log('No token, will empty headerConfig');
      headerConfig = {};
    }

    let url = `${api}/${apiVersion}/stories/${storyId}`;

    if (sceneTypes) {
      url += `?scenesType=${sceneTypes}`;
    }

    return _.get(url, headerConfig).then(response => response.data);
  }

  static async getStories(params) {
    let config = await this.validIdToken();

    // Set up filter parameters
    // We use offset instead of page numbers, so convert
    let offset = params.page * params.size;

    config.params = {
      q: params.searchTerm,
      offset: offset,
      limit: params.size,
      archived: params.archived,
    };

    if (params.omitFiledStories) {
      config.params.omitFiledStories = params.omitFiledStories;
    }

    if (params.folder) {
      config.params.folder = params.folder;
      config.params.omitFiledStories = false;
    }

    if (params.approvalStatus !== null && params.approvalStatus !== undefined) {
      config.params.approvalStatus = params.approvalStatus;
    }

    if (params.reviewing) {
      config.params.reviewing = params.reviewing;
    }

    return _.get(`${api}/${apiVersion}/stories`, config).then(response => response.data);
  }

  static async getStoriesV2(params) {
    let config = await this.validIdToken();

    // Set up filter parameters
    // We use offset instead of page numbers, so convert
    let offset = params.page * params.size;

    config.params = {
      q: params.searchTerm,
      offset: offset,
      limit: params.size,
      archived: params.archived,
      omitFiledStories: params.omitFiledStories,
      include_empty: params.include_empty,
    };

    if (params.folder) {
      config.params.folder = params.folder;
      config.params.omitFiledStories = false;
    }

    if (params.approvalStatus !== null && params.approvalStatus !== undefined) {
      config.params.approvalStatus = params.approvalStatus;
    }

    if (params.reviewing) {
      config.params.reviewing = params.reviewing;
    }

    if (params.tags) {
      config.params.tags = params.tags;
    }

    return _.get(`${api}/${apiVersion}/stories`, config).then(response => response.data);
  }

  static async createStory(storyId, sceneId, folderId, videoName, videoKey, videoDuration = null) {
    const userDetailsRaw = localStorage.getItem('userDetails');
    const userDetails = JSON.parse(userDetailsRaw);
    const credentials = await Auth.currentUserCredentials().catch(e => console.log(e));

    const videoPath = `${userDetails.bucket}/protected/${credentials.identityId}/${videoKey}`;

    let createDate = new Date();

    const body = {
      sceneId: sceneId,
      folderId: folderId,
      storyName: videoName,
      videoPath: videoPath,
      createDate: createDate.toISOString(),
      videoDuration: videoDuration,
    };

    const path = `${api}/${apiVersion}/story/${storyId}/render`;
    console.log('create path', path);

    let headerConfig = await this.validIdToken();

    const story = JSON.stringify(body);
    return _.post(`${api}/${apiVersion}/story/${storyId}/render`, story, headerConfig).then(response => response.data);
  }
  static async downloadStory(storyId, params) {
    let config = {};
    if (params) {
      config.params = params;
    }
    return _.get(`${api}/${apiVersion}/story/${storyId}/download`, config)
      .then(response => response.data)
      .catch(e => console.log(e));
  }

  static async insertStory(story) {
    let headerConfig = await this.validIdToken();
    return _.post(`${api}/${apiVersion}/stories`, story, headerConfig).then(response => response.data);
  }

  //   static async insertStory(story) {
  //     // let headerConfig = await this.idToken()
  //     const headers = await VormatsService.getHeaders();

  //     const userDetailsRaw = await browser.storage.local.get('userDetails').catch(e => console.log(e));
  //     const userDetails = JSON.parse(userDetailsRaw.userDetails);

  //     // Prep story object for insertion
  //     let cleanPath = story.scenes[0].path.split('?')[0].split('https://')[1]
  //     story.createDate = new Date().toISOString().slice(0, 19).replace('T', ' ')
  //     story.scenes[0].path = cleanPath
  //     story.username = userDetails.username
  //     story.video.path = cleanPath

  //     return _.post(`${api}/${apiVersion}/story`, story, headers).then(response => response.data)
  // }

  static async duplicateStory(storyId, folderId = null) {
    let headerConfig = await this.validIdToken();
    const params = {
      folderId: folderId,
    }
    return _.post(`${api}/${apiVersion}/story/${storyId}/copy`, params, headerConfig)
      .then(response => response.data)
      .catch(e => e);
  }

  static async updateStory(storyId, story, runMusicJob = null) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/story/${storyId}`, story, {
      params: { run_music_job: runMusicJob },
      ...headerConfig,
    }).then(response => response.data);
  }

  static async batchUpdateStory(payload) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/stories`, payload, {
      ...headerConfig,
    }).then(response => response.data);
  }

  static async updateStoryReviewStatus(storyId, story) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/stories/${storyId}/review`, story, headerConfig).then(response => response.data);
  }

  static async claimStory(storyId, folder) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/story/${storyId}/claim`, folder, headerConfig).then(response => response.data);
  }

  static async deleteStory(storyId, permanently = false) {
    let headerConfig = await this.validIdToken();
    return _.delete(`${api}/${apiVersion}/story/${storyId}?permanent=${permanently}`, headerConfig).then(response => response.data);
  }

  static async logEvent(storyId, event) {
    let headerConfig = { headers: {} };

    const isLoggedIn = await this.isLoggedIn();
    if (isLoggedIn) {
      headerConfig = await this.validIdToken();
    }
    let events = {
      events: [event],
    };
    return _.post(`${api}/${apiVersion}/events/${storyId}/log-event`, events, headerConfig).then(response => response.data);
  }

  static async getComments(storyId, type = 1) {
    return _.get(`${api}/${apiVersion}/story/${storyId}/comments?type=${type}`).then(response => response.data);
  }

  static async insertComment(storyId, comment) {
    let headerConfig = { headers: {} };
    if (await this.isLoggedIn()) {
      headerConfig = await this.validIdToken();
    }
    return _.post(`${api}/${apiVersion}/story/${storyId}/comments`, comment, headerConfig).then(response => response.data);
  }

  static async deleteComment(storyId, commentId) {
    let headerConfig = await this.validIdToken();
    return _.delete(`${api}/${apiVersion}/story/${storyId}/comments/${commentId}`, headerConfig).then(response => response.data);
  }

  static async updateComment(storyId, comment) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/story/${storyId}/comments/${comment.id}`, comment, headerConfig).then(
      response => response.data
    );
  }

  static async insertReaction(reaction) {
    let headerConfig = { headers: {} };
    if (await this.isLoggedIn()) {
      headerConfig = await this.validIdToken();
    }
    return _.post(`${api}/${apiVersion}/reactions`, reaction, headerConfig).then(response => response.data);
  }

  static async updateReaction(reaction) {
    let headerConfig = { headers: {} };
    if (await this.isLoggedIn()) {
      headerConfig = await this.validIdToken();
    }
    return _.put(`${api}/${apiVersion}/reactions`, reaction, headerConfig).then(response => response.data);
  }

  static async deleteReaction(reactionId) {
    let headerConfig = { headers: {} };
    if (await this.isLoggedIn()) {
      headerConfig = await this.validIdToken();
    }
    return _.delete(`${api}/${apiVersion}/reactions/${reactionId}`, headerConfig).then(response => response.data);
  }

  /**
   * Get a single folder for this user
   * @param {string} folderId The ID of the folder you want to retrieve
   * @return {object} { "result" => {...} }
   */
  static async getFolder(folderId) {
    let headerConfig = await this.validIdToken();
    return _.get(`${api}/${apiVersion}/folders/${folderId}`, headerConfig).then(response => response.data);
  }

  /**
   * Get all folders for this user
   * @return {object} { "result" => [{...}, {...}] }
   */
  static async getFolders(archived = null) {
    let headerConfig = await this.validIdToken();

    if (archived !== null) {
      headerConfig.params = {
        archived: archived,
      };
    }

    return _.get(`${api}/${apiVersion}/folders`, headerConfig).then(response => response.data);
  }

  /**
   * Create a new folder
   *
   * @param {object} folder The folder to create
   * @example
   * {
   *    "name": "Test Folder"
   * }
   *
   * @return {object} { "result" => true } on success
   */
  static async insertFolder(folder) {
    let headerConfig = await this.validIdToken();
    return _.post(`${api}/${apiVersion}/folders`, folder, headerConfig).then(response => response.data);
  }

  /**
   * Update a folder
   *
   * @param {string} folderId The ID of the folder you want to update
   * @param {object} folder The folder object with all properties that need updating (now only name supported)
   * @example
   * {
   *    "name": "Test Folder"
   * }
   *
   * @return {object} { "result" => true } on success
   */
  static async updateFolder(folderId, folder) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/folders/${folderId}`, folder, headerConfig).then(response => response.data);
  }

  /**
   * Delete a folder permanently
   *
   * @param {string} folderId The ID of the folder you want to delete
   * @return {object} { "result" => true } on success
   */
  static async deleteFolder(folderId) {
    let headerConfig = await this.validIdToken();
    return _.delete(`${api}/${apiVersion}/folders/${folderId}?rm_stories=1`, headerConfig).then(response => response.data);
  }

  /**
   * Add new users to the shared folder
   *
   * @param {string} folderId The ID of the folder you want to share
   * @param {object} usersInfo The info about new users (users array and message string)
   * @return {object} { "result" => true } on success
   */
  static async addUsersToFolder(folderId, usersInfo) {
    let headerConfig = await this.validIdToken();
    return _.post(`${api}/${apiVersion}/folders/${folderId}/users`, usersInfo, headerConfig).then(response => response.data);
  }

  /**
   * Edit user in shared folder
   *
   * @param {string} folderId The ID of the folder you want to edit
   * @param {string} username The user you want to edit
   * @param {string} role The user role you want to change
   * @return {object} { "result" => true } on success
   */
  static async editUserInSharedFolder(folderId, username, role) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/folders/${folderId}/users/${encodeURIComponent(username)}`, role, headerConfig).then(
      response => response.data
    );
  }

  /**
   * Delete user in shared folder
   *
   * @param {string} folderId The ID of the folder you want to edit
   * @param {string} username The user you want to delete
   * @return {object} { "result" => true } on success
   */
  static async deleteUserInSharedFolder(folderId, username) {
    let headerConfig = await this.validIdToken();
    return _.delete(`${api}/${apiVersion}/folders/${folderId}/users/${encodeURIComponent(username)}`, headerConfig).then(
      response => response.data
    );
  }

  /**
   * Start a job
   * This is used to generate all kinds of assets, like thumbnails, gifs, waveforms, subtitles etc,
   * depending on the config that has been passed in.
   *
   * The job will be queued and then processed by a Lambda function, so the response from this call
   * is async and you won't receive the result here, but have to poll getJobDetails() for that
   *
   * @see: https://vormats.atlassian.net/wiki/spaces/VORMATS/pages/805175297/Long-running+operations+API
   *
   * @returns Job ID, needed for polling the job status
   */
  static async addJob(config) {
    let headerConfig = await this.validIdToken();

    console.log('addJob config', config);

    return _.post(`${api}/${apiVersion}/jobs`, config, headerConfig).then(response => response.data);
  }

  /**
   * Get job details
   * This is used to check whether a job has finished processing yet, and if so, what the path to the output files is
   *
   * @returns Job Details model
   */
  static async getJobDetails(jobId) {
    let headerConfig = await this.validIdToken();

    return _.get(`${api}/${apiVersion}/jobs/${jobId}`, headerConfig).then(response => response.data);
  }

  /**
   * Cancel job with id
   * @param {*} jobId
   * @returns
   */
  static async cancelJob(jobId) {
    let headerConfig = await this.validIdToken();

    return _.delete(`${api}/${apiVersion}/jobs/${jobId}`, headerConfig).then(response => response.data);
  }

  /**
   * Check is jobs running
   * @param {*} job_type (optional)
   * @param {*} job_label (optional)
   * @param {*} scene_id (optional)
   * @returns
   */
  static async checkJobs(storyId, sceneId = null) {
    let headerConfig = await this.validIdToken();
    let query = '';
    if (sceneId) {
      query = `?scene_id=${sceneId}`;
    }
    return _.get(`${api}/${apiVersion}/story/${storyId}/jobs${query}`, headerConfig).then(response => response.data);
  }

  /**
   * Check story status
   * @param {*} storyId (optional)
   * @returns
   */
  static async checkStoryStatus(storyId) {
    let headerConfig = await this.validIdToken();
    return _.get(`${api}/${apiVersion}/stories/${storyId}/status`, headerConfig).then(response => response.data);
  }

  /**
   * Delete a resource
   */
  static async deleteResource(resourceId) {
    let headerConfig = await this.validIdToken();

    return _.delete(`${api}/${apiVersion}/resources/${resourceId}`, headerConfig).then(response => response.data);
  }

  /**
   * Update a resource
   *
   * @param {string} resourceId The ID of the resource you want to update
   * @param {object} resource The resource object with all properties that need updating
   * @example ```js
   * {
   *    "label": "newLabel",
   *    "properties": {
   *      "selected": true
   *    }
   * }
   * ```
   *
   * @return {object} { "result" => true } on success
   */
  static async updateResource(resourceId, resource) {
    let headerConfig = await this.validIdToken();

    return _.put(`${api}/${apiVersion}/resources/${resourceId}`, resource, headerConfig).then(response => response.data);
  }

  /**
   * Update multiple resources at a time
   *
   * @param {object} resources Array of resources, `id` is required for each array element
   * @example ```js
   *  {
   *     "resources": [
   *        {
   *           "id": "e3633504-9ba4-4577-b547-d9ca5cf6ff66",
   *           "label": "newLabel",
   *           "properties": {
   *              "selected": true
   *           }
   *      },
   *      ...
   *     ]
   *  }
   * ```
   */
  static async updateResources(resources) {
    let headerConfig = await this.validIdToken();

    return _.put(`${api}/${apiVersion}/resources`, resources, headerConfig).then(response => response.data);
  }

  static async getScene(sceneId) {
    let headerConfig = await this.validIdToken();

    return _.get(`${api}/${apiVersion}/scenes/${sceneId}`, headerConfig).then(response => response.data);
  }

  static async deleteScene(sceneId) {
    let headerConfig = await this.validIdToken();

    return _.delete(`${api}/${apiVersion}/scenes/${sceneId}`, headerConfig).then(response => response.data);
  }

  static async updateScene(sceneId, scene) {
    let headerConfig = await this.validIdToken();

    return _.put(`${api}/${apiVersion}/scenes/${sceneId}`, scene, headerConfig).then(response => response.data);
  }

  static async bulkUpdateScenes(scenes) {
    let headerConfig = await this.validIdToken();

    return _.put(`${api}/${apiVersion}/scenes`, { scenes }, headerConfig).then(response => response.data);
  }

  static async createScene(scene) {
    let headerConfig = await this.validIdToken();

    return _.post(`${api}/${apiVersion}/scenes`, scene, headerConfig).then(response => response.data);
  }

  /**
   * Copy an existing scene to a new story
   *
   * @param {string} originalSceneId
   * @param {object} config Should contain the ID of the new story as `storyId`, and optionally the ID of the newly copied scene as `sceneId`
   */
  static async duplicateScene(originalSceneId, config) {
    let headerConfig = await this.validIdToken();

    return _.post(`${api}/${apiVersion}/scenes/${originalSceneId}/copy`, config, headerConfig).then(response => response.data);
  }

  /**
   * Get hls generating status
   * This is used to check whether a job to generate hls has finished processing yet
   *
   */
  static async getStoryStatus(storyId) {
    let headerConfig = await this.validIdToken();

    return _.get(`${api}/${apiVersion}/story/${storyId}/save-status`, headerConfig).then(response => response.data);
  }

  /**
   *
   * @param {string} storyId
   * @param {object} subtitleConfig Expects:
   * {
   *  "identityId": "",
   *  "videoFileNames": [""]             // Array of scene video URLs for which you want to generate subtitles,
   *  "audioLanguage": ""
   * }
   */
  static async generateSubtitles(storyId, subtitleConfig) {
    let headerConfig = await this.validIdToken();
    console.log('headerConfig', headerConfig);

    return _.post(`${api}/${apiVersion}/story/${storyId}/subtitles`, subtitleConfig, headerConfig).then(response => response.data);
  }

  static async cancelSubtitles(storyId) {
    let headerConfig = await this.validIdToken();

    return _.get(`${api}/${apiVersion}/story/${storyId}/subtitles/cancel`, headerConfig).then(response => response.data);
  }

  static async getSubtitleStatus(storyId) {
    let headerConfig = await this.validIdToken();

    return _.get(`${api}/${apiVersion}/story/${storyId}/subtitles/status`, headerConfig).then(response => response.data);
  }

  /**
   *
   * @param {string} storyId
   * @param {object} translationConfig Expects:
   * {
   *  scenes: [
   *    "id": ""
   *    "from": "en",
   *    "to": ["nl", "it"]
   *  ]
   * }
   */
  static async translateSubtitles(storyId, translationConfig) {
    let headerConfig = await this.validIdToken();

    return _.post(`${api}/${apiVersion}/story/${storyId}/subtitles/translate`, translationConfig, headerConfig).then(
      response => response.data
    );
  }

  /**
   *
   * @param {object} subtitles Expects:
   * {
   *  "subtitles": [
   *    {
   *      "sceneId": "",
   *      "srt": "",
   *      "language": ""
   *    },
   *    ...
   *  ]
   * }
   */

  static async saveSubtitles(storyId, subtitles) {
    let headerConfig = await this.validIdToken();

    return _.put(`${api}/${apiVersion}/story/${storyId}/subtitles`, { subtitles }, headerConfig).then(response => response.data);
  }

  static async deleteSubtitles(storyId) {
    let headerConfig = await this.validIdToken();

    return _.delete(`${api}/${apiVersion}/story/${storyId}/subtitles`, headerConfig).then(response => response.data);
  }

  /**
   * Get URLs to upload resource directly to s3
   *
   * @param {object} resources Expects:
   *
   * {
   *  [
   *    {
   *      "uploadId": "{uploadId}". -- generated by the client to identify which files go where
   *      "sceneId": "UUID"  (optional)
   *      "resourceType": "textpanel"
   *    }
   *  ]
   * }
   *
   */

  static async getUploadUrl(storyId, payload) {
    let headerConfig = await this.validIdToken();
    return _.post(`${api}/${apiVersion}/story/${storyId}/upload-urls`, payload, headerConfig).then(response => response.data);
  }

  static async checkFileInS3(path) {
    let headerConfig = await this.validIdToken();
    return _.get(`${api}/${apiVersion}/files?path=${path}`, headerConfig).then(response => response.data);
  }

  static async applyStoryVoiceovers(storyId) {
    let headerConfig = await this.validIdToken();
    return _.put(`${api}/${apiVersion}/stories/${storyId}/apply-audio`, {}, headerConfig).then(response => response.data);
  }
}

export default CmsAPI;
